import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiClient } from '@/lib/api-client';
import { extractServerError } from '@/lib/api-error';

/** Meta ad spend import (apps/api meta-ads module). */

export interface MetaAdSyncStatus {
  enabled: boolean;
  apiVersion: string;
  lookbackDays: number;
  latestDataDay: string | null;
  /** An import is in flight right now. */
  running: boolean;
  accounts: Array<{
    accountId: string;
    label: string | null;
    hasAccessToken: boolean;
    lastSuccessAt: string | null;
    lastRun: {
      status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
      trigger: string;
      startedAt: string;
      finishedAt: string | null;
      rows: number;
      error: string | null;
    } | null;
  }>;
}

export type AdPerformanceLevel = 'campaign' | 'adset' | 'ad';

export interface AdPerformanceRow {
  id: string;
  campaignId: string | null;
  campaignName: string | null;
  adsetId: string | null;
  adsetName: string | null;
  adId: string | null;
  adName: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  linkClicks: number;
  platformLeads: number;
  crmLeads: number;
  crmWon: number;
  costPerPlatformLead: number | null;
  costPerCrmLead: number | null;
  ctr: number | null;
}

export interface AdPerformance {
  from: string;
  to: string;
  level: AdPerformanceLevel;
  rows: AdPerformanceRow[];
  totals: {
    spend: number;
    impressions: number;
    clicks: number;
    platformLeads: number;
    crmLeads: number;
    crmWon: number;
    costPerPlatformLead: number | null;
    costPerCrmLead: number | null;
  };
}

export const useMetaAdSyncStatus = () =>
  useQuery({
    queryKey: ['marketing', 'meta-ads', 'status'],
    queryFn: async () =>
      (await apiClient.get<MetaAdSyncStatus>('/reports/marketing/ad-sync/status')).data,
    staleTime: 60_000,
    // While an import runs, keep asking until it finishes.
    refetchInterval: (query) => (query.state.data?.running ? 5_000 : false),
  });

export const useAdPerformance = (
  range: { from: string; to: string } | null,
  level: AdPerformanceLevel,
) =>
  useQuery({
    queryKey: ['marketing', 'meta-ads', 'performance', range, level],
    queryFn: async () =>
      (
        await apiClient.get<AdPerformance>('/reports/marketing/ad-performance', {
          params: { ...range, level },
        })
      ).data,
    enabled: range !== null,
    staleTime: 60_000,
  });

/**
 * Starts the import and returns at once. Reading months of ads out of Meta
 * takes longer than a request may wait, so the panel follows the status
 * afterwards and refreshes the figures when it finishes.
 */
export const useSyncMetaAds = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (range: { from?: string; to?: string }) =>
      (
        await apiClient.post<{
          started: boolean;
          alreadyRunning: boolean;
          from: string;
          to: string;
        }>('/reports/marketing/ad-sync', range)
      ).data,
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['marketing', 'meta-ads', 'status'] });
      toast.success(
        result.alreadyRunning
          ? 'An import is already running. The figures refresh when it finishes.'
          : `Importing ${result.from} to ${result.to} from Meta. This takes a few minutes.`,
      );
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Could not sync Meta ad spend'),
  });
};
